/*
 * Copyright (c) 2026 GS Elektromedizinische Geraete G. Stemple GmbH, Germany - all rights reserved.
 * <p>
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 */

import { ReactNode, useContext, useEffect } from 'react';
import { Typography } from '@mui/material';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import HelpOutlinedIcon from '@mui/icons-material/HelpOutlined';
import style from './DiviFieldCard.scss?inline';
import ToggleButton from '../toggleButton/ToggleButton';
import { useCSS } from '../../../provider/CSSProvider';
import { useDiviPageContext } from '../DiviPage/DiviPage';
import { CardId, getCardTitle } from '../../../models/diviCard';
import HeartPlusIcon from '../../../assets/images/icons/heart-plus.svg?react';
import HeartCheckedIcon from '../../../assets/images/icons/heart-checked.svg?react';
import { useTheme } from '@mui/material/styles';
import { useFreshCallback } from '../../../utils/hooks';
import { NodeRecordValueTypeMap } from '../../../backendModels/records.model';
import { ReportsAPIContext } from '../../../provider/ReportsAPIProvider';
import { NodeType } from '../../../backendModels/report.model';
import { getEnhancedInputState } from '../../../models/custom';
import { InputState } from '../../../backendModels/general.model';

interface DiviFieldCardProps {
  /**
   * The type of the card.
   */
  readonly cardType: CardId;
  /**
   * Custom title content when title is not a text.
   */
  readonly customTitleContent?: ReactNode;
  /**
   * Sub-Title text.
   */
  readonly subtitle?: string;
  /**
   * Is invoked when the noPathologicalFindingsButton is clicked.
   */
  readonly onNormalStateButtonClicked?: () => void;
  /**
   * Displays "ohne path. Befund" button when true. Defaults to onNormalStateButtonClicked != null.
   */
  readonly normalStateButtonVisible?: boolean;
  /**
   * Label displayed on the normal state text button. Defaults to 'ohne path. Befund'
   */
  readonly normalStateButtonLabel?: string;
  /**
   * Is invoked when the notAccessibleButton is clicked.
   */
  readonly onUnknownStateButtonClicked?: () => void;
  /**
   * Displays "nicht beurteilbar" button when true. Defaults to onUnknownStateButtonClicked != null.
   */
  readonly unknownStateButtonVisible?: boolean;
  /**
   * Label displayed on the unknown state text button. Defaults to 'nicht beurteilbar'.
   */
  readonly unknownStateButtonLabel?: string;

  /**
   * The node type used to determine the input state
   */
  readonly nodeType?: NodeType;
  /**
   * The record type used to determine the input state
   */
  readonly recordType?: keyof NodeRecordValueTypeMap;
  /**
   * The elementKey for generic records
   */
  readonly elementKey?: string;
  /**
   * Highlight the border in black to signal e.g. a warning. Defaults to false.
   */
  readonly highlightBorder?: boolean;
  readonly children?: ReactNode;
}

export default function DiviFieldCard(props: DiviFieldCardProps) {
  useCSS(style);
  const theme = useTheme();

  const {
    cardType,
    customTitleContent,
    subtitle,
    onNormalStateButtonClicked,
    onUnknownStateButtonClicked,
    normalStateButtonVisible,
    unknownStateButtonVisible,
    normalStateButtonLabel,
    unknownStateButtonLabel,
    recordType,
    elementKey,
    nodeType,
    highlightBorder,
    children,
  } = props;

  const { findMultipleRecords } = useContext(ReportsAPIContext);
  const records = recordType && nodeType ? findMultipleRecords(recordType, nodeType, elementKey) : [];

  const { subscribeToReset, unsubscribeFromReset } = useDiviPageContext();

  const inputState = recordType != null && nodeType != null ? getEnhancedInputState(recordType, records) : null;

  const handleNormalStateButtonClicked = useFreshCallback(() => {
    if (onNormalStateButtonClicked) {
      onNormalStateButtonClicked();
    }
  });

  const handleUnknownStateButtonClicked = useFreshCallback(() => {
    if (onUnknownStateButtonClicked) {
      onUnknownStateButtonClicked();
    }
  });

  useEffect(() => {
    subscribeToReset(handleNormalStateButtonClicked);
    return () => unsubscribeFromReset(handleNormalStateButtonClicked);
  }, [subscribeToReset, unsubscribeFromReset, handleNormalStateButtonClicked]);

  return (
    <div id={`card-${cardType.toString()}`} className={`card${highlightBorder ? ' highlight-border' : ''}`}>
      {customTitleContent === undefined ? (
        <div className='title-content'>
          <Typography align='left' variant='h2'>
            {getCardTitle(cardType)}
          </Typography>
        </div>
      ) : (
        customTitleContent
      )}
      {subtitle && (
        <Typography align='left' className='title-subtitle' variant='subtitle2'>
          {subtitle}
        </Typography>
      )}
      {((normalStateButtonVisible ?? onNormalStateButtonClicked) ||
        (unknownStateButtonVisible ?? onUnknownStateButtonClicked)) && (
        <div className='divi-field-card-button-row'>
          {(normalStateButtonVisible ?? onNormalStateButtonClicked) && (
            <ToggleButton
              onClick={handleNormalStateButtonClicked}
              disableElevation
              selected={inputState === InputState.NORMAL}
              color='secondary'
              selectedIcon={<HeartCheckedIcon fill={theme.palette.secondary.main} />}
              unselectedIcon={<HeartPlusIcon fill={theme.palette.secondary.main} />}
              className='no-path-button'
              variant={inputState === InputState.NORMAL ? 'contained' : 'text'}
              data-testid={`${props.cardType}-input-state-normal`}>
              {normalStateButtonLabel ?? 'ohne path. Befund'}
            </ToggleButton>
          )}
          {(unknownStateButtonVisible ?? onUnknownStateButtonClicked) && (
            <ToggleButton
              onClick={handleUnknownStateButtonClicked}
              disableElevation
              selected={inputState === InputState.UNKNOWN}
              color='info'
              selectedIcon={<HelpOutlinedIcon color='info' />}
              unselectedIcon={<HelpOutlineOutlinedIcon color='info' />}
              className='unknown-button'
              variant={inputState === InputState.UNKNOWN ? 'contained' : 'text'}
              data-testid={`${props.cardType}-input-state-unknown`}>
              {unknownStateButtonLabel ?? 'nicht beurteilbar'}
            </ToggleButton>
          )}
        </div>
      )}
      <div className='main-content'>{children}</div>
    </div>
  );
}
